'use client';

import {useEffect, useRef, useState} from 'react';
import './TetrisPost.scss';

const Frame = ({path, pills = [], caption, children}) => <figure className='tp-fig'>
  <div className='tp-fig-frame'>
    <div className='tp-fig-chrome'>
      <span className='tp-fig-dot'/>
      <span className='tp-fig-path'>{path}</span>
      <span className='tp-fig-right'>
        {pills.map(([label, color]) => <span className={`tp-fig-pill ${color || ''}`} key={label}>{label}</span>)}
      </span>
    </div>
    {children}
  </div>
  {caption && <figcaption>{caption}</figcaption>}
</figure>;

// runs fn once the canvas mounts and stops its animation loop on unmount
const useCanvas = fn => {
  const ref = useRef(null);
  useEffect(() => fn(ref.current), []);
  return ref;
};

const SHAPES = {
  I: [[1, 1, 1, 1]], O: [[1, 1], [1, 1]], T: [[0, 1, 0], [1, 1, 1]],
  S: [[0, 1, 1], [1, 1, 0]], Z: [[1, 1, 0], [0, 1, 1]],
  J: [[1, 0, 0], [1, 1, 1]], L: [[0, 0, 1], [1, 1, 1]]
};
const COLORS = {
  I: '#4ecdc4', O: '#e0bd45', T: '#a97bd8', S: '#68bf5c',
  Z: '#e0614f', J: '#5b8ff0', L: '#e0913f'
};
const KEYS = Object.keys(SHAPES);

const runTetris = (cv, onScore) => {
  const x = cv.getContext('2d');
  const CW = 900, CH = 380, W = 10, HH = 20, CELL = 17;
  const FX = (CW - W * CELL) / 2, FY = (CH - HH * CELL) / 2;
  const emptyBoard = () => Array.from({length: HH}, () => new Array(W).fill(null));

  let board = emptyBoard(), score = 0, lines = 0, queue = [], cur = null, tgt = null, frame = 0, raf;

  const rot = m => {
    const r = [], h = m.length, w = m[0].length;
    for (let y = 0; y < w; y++) {
      r.push([]);
      for (let z = 0; z < h; z++) r[y].push(m[h - 1 - z][y]);
    }
    return r;
  };
  const fits = (b, m, ox, oy) => {
    for (let y = 0; y < m.length; y++) for (let z = 0; z < m[y].length; z++) {
      if (!m[y][z]) continue;
      const X = ox + z, Y = oy + y;
      if (X < 0 || X >= W || Y >= HH) return false;
      if (Y >= 0 && b[Y][X]) return false;
    }
    return true;
  };
  const stamp = (b, m, ox, oy, k) => {
    const nb = b.map(r => r.slice());
    for (let y = 0; y < m.length; y++) for (let z = 0; z < m[y].length; z++) {
      if (m[y][z] && oy + y >= 0) nb[oy + y][ox + z] = k;
    }
    return nb;
  };
  const clearRows = b => {
    const keep = b.filter(r => r.some(c => !c));
    const n = HH - keep.length;
    return {b: Array.from({length: n}, () => new Array(W).fill(null)).concat(keep), n};
  };
  const evaluate = (b, n) => {
    const hs = [];
    let holes = 0;
    for (let c = 0; c < W; c++) {
      let h = 0, seen = false;
      for (let r = 0; r < HH; r++) {
        if (b[r][c]) {
          if (!seen) { h = HH - r; seen = true; }
        } else if (seen) holes++;
      }
      hs.push(h);
    }
    let agg = 0, bump = 0;
    for (let i = 0; i < W; i++) agg += hs[i];
    for (let j = 0; j < W - 1; j++) bump += Math.abs(hs[j] - hs[j + 1]);
    return -0.51 * agg + 0.76 * n - 0.36 * holes - 0.18 * bump;
  };
  const plan = k => {
    let best = null, m = SHAPES[k];
    for (let r = 0; r < 4; r++) {
      for (let ox = -2; ox < W; ox++) {
        if (!fits(board, m, ox, 0)) continue;
        let oy = 0;
        while (fits(board, m, ox, oy + 1)) oy++;
        const res = clearRows(stamp(board, m, ox, oy, k));
        const sc = evaluate(res.b, res.n);
        if (!best || sc > best.sc) best = {sc, rot: r, ox, oy};
      }
      m = rot(m);
    }
    return best;
  };
  const refill = () => { while (queue.length < 4) queue.push(KEYS[(Math.random() * 7) | 0]); };
  const spawn = () => {
    refill();
    const k = queue.shift();
    let p = plan(k);
    if (!p) {
      board = emptyBoard();
      score = 0; lines = 0;
      onScore(score, lines);
      p = plan(k);
    }
    let m = SHAPES[k];
    for (let r = 0; r < p.rot; r++) m = rot(m);
    cur = {k, m, x: Math.floor((W - m[0].length) / 2), y: -m.length};
    tgt = p;
  };

  const tick = () => {
    if (!cur) { spawn(); return; }
    if (frame % 3 === 0 && cur.x !== tgt.ox) { cur.x += cur.x < tgt.ox ? 1 : -1; return; }
    if (frame % 2 === 0) {
      if (fits(board, cur.m, cur.x, cur.y + 1)) cur.y++;
      else {
        const res = clearRows(stamp(board, cur.m, cur.x, cur.y, cur.k));
        board = res.b;
        if (res.n) {
          lines += res.n;
          score += [0, 100, 300, 500, 800][res.n];
          onScore(score, lines);
        }
        cur = null;
      }
    }
  };

  const block = (px, py, color, s = CELL) => {
    x.fillStyle = color; x.fillRect(px + 1, py + 1, s - 2, s - 2);
    x.fillStyle = 'rgba(255,255,255,.20)'; x.fillRect(px + 1, py + 1, s - 2, 2);
    x.fillStyle = 'rgba(0,0,0,.22)'; x.fillRect(px + 1, py + s - 3, s - 2, 2);
  };
  const panelLabel = (px, py, t) => {
    x.textAlign = 'left';
    x.font = '10px ui-monospace,SFMono-Regular,Menlo,monospace';
    x.fillStyle = '#6f7a89'; x.fillText(t, px, py);
  };
  const panelValue = (px, py, t, col) => {
    x.textAlign = 'left';
    x.font = '600 26px ui-monospace,SFMono-Regular,Menlo,monospace';
    x.fillStyle = col; x.fillText(t, px, py);
  };

  const draw = () => {
    x.fillStyle = '#12151a'; x.fillRect(0, 0, CW, CH);

    // playfield
    x.fillStyle = '#171b22'; x.fillRect(FX, FY, W * CELL, HH * CELL);
    x.strokeStyle = '#2c3440'; x.lineWidth = 1;
    x.strokeRect(FX - .5, FY - .5, W * CELL + 1, HH * CELL + 1);
    x.strokeStyle = '#1d232c';
    for (let c = 1; c < W; c++) {
      x.beginPath(); x.moveTo(FX + c * CELL, FY); x.lineTo(FX + c * CELL, FY + HH * CELL); x.stroke();
    }
    for (let r = 1; r < HH; r++) {
      x.beginPath(); x.moveTo(FX, FY + r * CELL); x.lineTo(FX + W * CELL, FY + r * CELL); x.stroke();
    }

    for (let y = 0; y < HH; y++) for (let z = 0; z < W; z++) {
      if (board[y][z]) block(FX + z * CELL, FY + y * CELL, COLORS[board[y][z]]);
    }

    if (cur) {
      // ghost
      let gy = cur.y;
      while (fits(board, cur.m, cur.x, gy + 1)) gy++;
      x.strokeStyle = 'rgba(255,255,255,.13)';
      for (let a = 0; a < cur.m.length; a++) for (let b = 0; b < cur.m[a].length; b++) {
        if (cur.m[a][b] && gy + a >= 0) {
          x.strokeRect(FX + (cur.x + b) * CELL + 1.5, FY + (gy + a) * CELL + 1.5, CELL - 3, CELL - 3);
        }
      }
      for (let p = 0; p < cur.m.length; p++) for (let q = 0; q < cur.m[p].length; q++) {
        if (cur.m[p][q] && cur.y + p >= 0) block(FX + (cur.x + q) * CELL, FY + (cur.y + p) * CELL, COLORS[cur.k]);
      }
    }

    // left panel
    const lx = FX - 250;
    panelLabel(lx, FY + 14, 'SCORE');
    panelValue(lx, FY + 44, String(score), '#e7ecf3');
    panelLabel(lx, FY + 92, 'LINES');
    panelValue(lx, FY + 122, String(lines), '#5fbf8c');
    panelLabel(lx, FY + 170, 'LEVEL');
    panelValue(lx, FY + 200, String(1 + Math.floor(lines / 10)), '#8fbcf7');

    // right panel, next queue
    const rx = FX + W * CELL + 64;
    panelLabel(rx, FY + 14, 'NEXT');
    refill();
    for (let n = 0; n < 3; n++) {
      const m = SHAPES[queue[n]], sz = 13;
      const oy = FY + 34 + n * 62;
      x.fillStyle = '#171b22'; x.fillRect(rx, oy, 4 * sz + 10, 2 * sz + 10);
      x.strokeStyle = '#232935'; x.strokeRect(rx + .5, oy + .5, 4 * sz + 10, 2 * sz + 10);
      const w = m[0].length * sz, h = m.length * sz;
      const bx = rx + (4 * sz + 10 - w) / 2, by = oy + (2 * sz + 10 - h) / 2;
      for (let i = 0; i < m.length; i++) for (let j = 0; j < m[i].length; j++) {
        if (m[i][j]) block(bx + j * sz, by + i * sz, COLORS[queue[n]], sz);
      }
    }
  };

  const loop = () => { frame++; tick(); draw(); raf = requestAnimationFrame(loop); };
  spawn(); loop();
  return () => cancelAnimationFrame(raf);
};

export const TetrisGame = () => {
  const [stats, setStats] = useState({score: 0, lines: 0});
  const ref = useCanvas(cv => runTetris(cv, (score, lines) => setStats({score, lines})));
  return <Frame path='/views/tetris' pills={[['Cylerian document', 'blue']]}>
    <div className='tp-fig-game'><canvas ref={ref} width={900} height={380}/></div>
    <div className='tp-fig-gamebar'>
      <span className='tp-fig-sc'>SCORE <b>{stats.score}</b></span>
      <span className='tp-fig-sc'>LINES <b>{stats.lines}</b></span>
      <span className='tp-fig-hint'>Attract mode · rendering in the browser</span>
    </div>
  </Frame>;
};

const TURNS = [
  ['TURN 1', '"Build Tetris. Pieces fall, arrow keys move and rotate them, a full row disappears."', 'Rendered in 5s. Playable on the first pass.'],
  ['TURN 7', '"Track high scores per user and show a leaderboard beside the board."', 'Rendered in 6s. The document is now reading and writing platform data.'],
  ['TURN 11', '"Make it head to head. Show the other player\'s board next to mine."', 'Rendered in 9s. Shared state across sessions, same file.']
];

export const PromptStrip = () => <Frame
  path='/views/tetris · editor' pills={[['3 of 11 turns']]}
  caption={<><b>Figure 1.</b> Three turns from the session that produced the document above. At turn 11, going from a single-player board to shared live state across sessions took one instruction.</>}>
  <div className='tp-fig-strip'>
    {TURNS.map(([n, q, r]) => <div className='tp-fig-cell' key={n}>
      <div className='tp-fig-n'>{n}</div>
      <div className='tp-fig-q'>{q}</div>
      <div className='tp-fig-r'>{r}</div>
    </div>)}
  </div>
</Frame>;

const BARS = [
  ['Critical', 9, 14, ''], ['High', 22, 61, ''], ['Medium', 48, 203, 'warn'],
  ['Low', 78, 418, 'warn'], ['Info', 100, '1,092', 'warn']
];

export const MdxSplit = () => <Frame
  path='/views/client/northwind-summary.mdx'
  caption={<><b>Figure 2.</b> Query, text, and rendering in a single file. The query runs against whatever the author is entitled to read.</>}>
  <div className='tp-fig-split'>
    <div className='tp-fig-pane'>
      <div className='tp-fig-paneh'>Source</div>
      <pre className='tp-fig-pre'><span className='c'>{'// runs against entitled data'}</span>{'\n'}
        <span className='k'>const</span> rows = <span className='k'>await</span> <span className='t'>query</span>{'({\n  index: '}<span className='s'>&apos;alerts&apos;</span>{',\n  filter: '}<span className='s'>&apos;org = &quot;northwind&quot;&apos;</span>{',\n  since: '}<span className='s'>&apos;30d&apos;</span>{',\n  groupBy: '}<span className='s'>&apos;severity&apos;</span>{'\n});\n\n'}
        <span className='a'>## Alert volume by severity</span>{'\n'}
        <span className='c'>Last 30 days, Northwind Trading</span>{'\n\n<'}
        <span className='t'>BarList</span>{'\n  data={rows}\n  label='}<span className='s'>&quot;severity&quot;</span>{'\n  value='}<span className='s'>&quot;count&quot;</span>{' />'}</pre>
    </div>
    <div className='tp-fig-pane'>
      <div className='tp-fig-paneh'>Rendered</div>
      <div className='tp-fig-rendered'>
        <div className='tp-fig-rv-title'>Alert volume by severity</div>
        <div className='tp-fig-rv-sub'>Last 30 days, Northwind Trading</div>
        <div className='tp-fig-bars'>
          {BARS.map(([label, pct, n, cls]) => <div className='tp-fig-bar' key={label}>
            <span>{label}</span>
            <span className='tp-fig-tr'><span className={`tp-fig-fl ${cls}`} style={{width: `${pct}%`}}/></span>
            <span className='tp-fig-bn'>{n}</span>
          </div>)}
        </div>
      </div>
    </div>
  </div>
</Frame>;

const KPIS = [
  ['Alerts received', '1,788', '−12% vs prior week', 'up'],
  ['Closed without analyst', '91%', '+4 pts', 'up'],
  ['Escalated', '23', '6 still open', ''],
  ['Median time to timeline', '2m 41s', '+18s', 'dn']
];
const ITEMS = [
  ['Suspicious attachment executed', 'Escalated · awaiting client confirmation', 'FIN-WS-04', '4h', 'Approve isolation', 'warn'],
  ['Repeated failed logins', 'Source outside client ranges', 'svc-backup', '11h', 'Reset credential', ''],
  ['Unmanaged device on VLAN 40', 'Pending client decision', 'n/a', '2d', 'Open ticket', '']
];

export const WeeklySummary = () => <Frame
  path='/views/client/northwind-weekly' pills={[['Built in one session'], ['Live', 'green']]}
  caption={<><b>Figure 3.</b> Same system and same build process as the game, pointed at a client deliverable. The controls act on the platform rather than linking out to it.</>}>
  <div className='tp-fig-dashhd'>
    <div>
      <div className='tp-fig-dt'>Northwind Trading weekly security summary</div>
      <div className='tp-fig-ds'>Week of Aug 31 · shared with the client&apos;s IT lead</div>
    </div>
    <span className='tp-fig-pill blue'>Auto-refreshes</span>
  </div>
  <div className='tp-fig-kpis'>
    {KPIS.map(([l, v, d, cls]) => <div className='tp-fig-kpi' key={l}>
      <div className='tp-fig-kl'>{l}</div>
      <div className='tp-fig-kv'>{v}</div>
      <div className={`tp-fig-kd ${cls}`}>{d}</div>
    </div>)}
  </div>
  <div className='tp-fig-tblwrap'>
    <table className='tp-fig-tbl'>
      <thead><tr><th>Open item</th><th>Host</th><th>Age</th><th className='r'>Action</th></tr></thead>
      <tbody>
        {ITEMS.map(([nm, ds, host, age, action, cls]) => <tr key={nm}>
          <td><div className='tp-fig-nm'>{nm}</div><div className='tp-fig-sub'>{ds}</div></td>
          <td>{host}</td>
          <td>{age}</td>
          <td className='r'><button type='button' className={`tp-fig-abtn ${cls}`}>{action}</button></td>
        </tr>)}
      </tbody>
    </table>
  </div>
  <div className='tp-fig-dashfoot'>Each control calls the platform API directly, within the permissions of the person using the document.</div>
</Frame>;

const ACCENTS = ['#5b8ff0', '#4ecdc4', '#a97bd8', '#5fbf8c', '#e0bd45', '#e0913f', '#e0614f'];
const TITLES = ['Client posture overview', 'Endpoint health', 'Triage throughput',
  'Patch compliance', 'Detection coverage', 'Identity risk', 'Backup status',
  'Network egress', 'Vulnerability aging', 'SLA performance', 'Ticket volume',
  'Asset inventory', 'MDR summary', 'Log ingestion', 'Firewall activity',
  'User behavior', 'Certificate expiry', 'Shadow IT discovery',
  'Onboarding progress', 'Threat intel matches', 'Failed logins by region',
  'Service availability', 'Compliance drift', 'Disk utilization',
  'Agent deployment', 'Quarantine review', 'DNS anomalies', 'Privileged access'];
const SUBS = ['Last 7 days', 'Last 30 days', 'Rolling 24h', 'This quarter',
  'All tenants', 'Per client', 'Northwind Trading', 'Acme Manufacturing',
  'Week to date', 'Since last review'];
const PANEL = ['Volume', 'Trend', 'By severity', 'By client', 'Coverage', 'Top hosts',
  'Aging', 'Throughput', 'Status', 'Breakdown', 'Activity', 'Recent', 'Distribution',
  'Compliance', 'Utilization', 'Queue', 'By region', 'Open items'];

const runGenerator = cv => {
  const x = cv.getContext('2d');
  const CW = 900, CH = 400, PAD = 12, HEAD = 44;
  let seed = Date.now() & 0x7fffffff, acc = ACCENTS[0], leaves = [], title = '', sub = '', raf;

  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const ri = (a, b) => a + Math.floor(rnd() * (b - a + 1));
  const pick = a => a[Math.floor(rnd() * a.length)];
  const series = (k, lo, hi) => Array.from({length: k}, () => lo + rnd() * (hi - lo));

  const split = (r, depth, out, forced) => {
    const w = r[2], h = r[3];
    if (depth <= 0 || w < 210 || h < 95 || (forced <= 0 && rnd() < 0.20)) { out.push(r); return; }
    const vert = Math.abs(w / h - 1) > 0.25 ? (w / h > 1.25) : rnd() < 0.5;
    const f = 0.36 + rnd() * 0.28;
    if (vert) {
      const cw = Math.floor(w * f);
      if (cw < 105 || w - cw < 105) { out.push(r); return; }
      split([r[0], r[1], cw, h], depth - 1, out, forced - 1);
      split([r[0] + cw, r[1], w - cw, h], depth - 1, out, forced - 1);
    } else {
      const ch = Math.floor(h * f);
      if (ch < 70 || h - ch < 70) { out.push(r); return; }
      split([r[0], r[1], w, ch], depth - 1, out, forced - 1);
      split([r[0], r[1] + ch, w, h - ch], depth - 1, out, forced - 1);
    }
  };

  // widget renderers, all draw inside (px,py,w,h)
  const wBars = (px, py, w, h) => {
    const d = series(ri(5, 11), 0.15, 1), bw = w / d.length;
    d.forEach((v, i) => {
      const bh = (h - 6) * v;
      x.fillStyle = i === d.length - 1 ? acc : 'rgba(145,160,180,.42)';
      x.fillRect(px + i * bw + bw * 0.18, py + h - bh, bw * 0.64, bh);
    });
  };
  const wHBars = (px, py, w, h) => {
    const d = series(ri(3, 6), 0.2, 1), bh = h / d.length;
    d.forEach((v, i) => {
      x.fillStyle = 'rgba(145,160,180,.18)';
      x.fillRect(px, py + i * bh + bh * 0.22, w, bh * 0.5);
      x.fillStyle = acc; x.fillRect(px, py + i * bh + bh * 0.22, w * v, bh * 0.5);
    });
  };
  const wLine = (px, py, w, h, fill) => {
    const d = series(ri(10, 22), 0.1, 0.95), sx = w / (d.length - 1);
    const path = () => {
      x.beginPath();
      d.forEach((v, i) => { const X = px + i * sx, Y = py + h - h * v; i ? x.lineTo(X, Y) : x.moveTo(X, Y); });
    };
    path();
    if (fill) {
      x.lineTo(px + w, py + h); x.lineTo(px, py + h); x.closePath();
      x.fillStyle = acc + '33'; x.fill();
      path();
    }
    x.strokeStyle = acc; x.lineWidth = 1.6; x.stroke();
  };
  const wDonut = (px, py, w, h) => {
    const cx = px + w / 2, cy = py + h / 2, r = Math.min(w, h) / 2 - 4;
    const d = series(ri(3, 5), 0.1, 1), t = d.reduce((p, c) => p + c, 0);
    let a = -Math.PI / 2;
    d.forEach((v, i) => {
      const sw = v / t * Math.PI * 2;
      x.beginPath(); x.arc(cx, cy, r, a, a + sw); x.arc(cx, cy, r * 0.56, a + sw, a, true);
      x.closePath();
      x.fillStyle = i ? `rgba(145,160,180,${0.42 - i * 0.07})` : acc; x.fill();
      a += sw;
    });
  };
  const wKPI = (px, py, w, h) => {
    const v = ri(2, 99), unit = pick(['%', '', 'ms', 'k']);
    x.textAlign = 'left';
    x.font = `600 ${Math.min(38, Math.floor(h * 0.5))}px ui-monospace,Menlo,monospace`;
    x.fillStyle = '#e7ecf3'; x.fillText(v + unit, px, py + h * 0.62);
    x.font = '10px ui-monospace,Menlo,monospace';
    x.fillStyle = rnd() < 0.5 ? '#5fbf8c' : '#e0614f';
    x.fillText(`${rnd() < 0.5 ? '+' : '-'}${ri(1, 24)}% vs prior`, px, py + h * 0.86);
  };
  const wKPIGrid = (px, py, w, h) => {
    for (let i = 0; i < 4; i++) {
      const cx = px + (i % 2) * (w / 2), cy = py + Math.floor(i / 2) * (h / 2);
      x.font = '10px ui-monospace,Menlo,monospace'; x.textAlign = 'left';
      x.fillStyle = '#6f7a89'; x.fillText(pick(PANEL).slice(0, 9), cx, cy + 11);
      x.font = '600 16px ui-monospace,Menlo,monospace';
      x.fillStyle = i === 0 ? acc : '#e7ecf3'; x.fillText(String(ri(3, 999)), cx, cy + 30);
    }
  };
  const wTable = (px, py, w, h) => {
    const rows = Math.min(6, Math.floor(h / 17));
    for (let i = 0; i < rows; i++) {
      const ry = py + i * 17;
      if (i % 2 === 0) { x.fillStyle = 'rgba(145,160,180,.05)'; x.fillRect(px, ry, w, 15); }
      x.fillStyle = 'rgba(180,192,208,.55)'; x.fillRect(px + 2, ry + 6, w * (0.22 + rnd() * 0.4), 3);
      x.fillStyle = i === 0 ? acc : 'rgba(145,160,180,.35)';
      x.fillRect(px + w - 30, ry + 6, 26 * (0.3 + rnd() * 0.7), 3);
    }
  };
  const wHeat = (px, py, w, h) => {
    const cols = Math.min(18, Math.floor(w / 13)), rows = Math.min(7, Math.floor(h / 13));
    const cw = w / cols, ch = h / rows;
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const v = rnd();
      x.fillStyle = v > 0.72 ? acc : `rgba(145,160,180,${0.08 + v * 0.3})`;
      x.fillRect(px + c * cw + 1, py + r * ch + 1, cw - 2.5, ch - 2.5);
    }
  };
  const wGauge = (px, py, w, h) => {
    const cx = px + w / 2, cy = py + h * 0.78, r = Math.min(w / 2, h * 0.72) - 4;
    x.lineWidth = Math.max(6, r * 0.22);
    x.strokeStyle = 'rgba(145,160,180,.18)';
    x.beginPath(); x.arc(cx, cy, r, Math.PI, 0); x.stroke();
    const f = 0.2 + rnd() * 0.75;
    x.strokeStyle = acc; x.beginPath(); x.arc(cx, cy, r, Math.PI, Math.PI + Math.PI * f); x.stroke();
    x.textAlign = 'center'; x.font = '600 15px ui-monospace,Menlo,monospace';
    x.fillStyle = '#e7ecf3'; x.fillText(`${Math.round(f * 100)}%`, cx, cy - 2);
  };
  const wStack = (px, py, w, h) => {
    const k = ri(5, 9), bw = w / k;
    for (let i = 0; i < k; i++) {
      const segs = ri(2, 4);
      let rem = h - 8, top = py + h;
      for (let j = 0; j < segs; j++) {
        const sh = rem * (j === segs - 1 ? 1 : 0.2 + rnd() * 0.4);
        rem -= sh; top -= sh;
        x.fillStyle = j === 0 ? acc : `rgba(145,160,180,${0.4 - j * 0.09})`;
        x.fillRect(px + i * bw + bw * 0.2, top, bw * 0.6, sh - 1);
      }
    }
  };
  const wScatter = (px, py, w, h) => {
    const n = ri(18, 42);
    for (let i = 0; i < n; i++) {
      x.fillStyle = rnd() > 0.8 ? acc : 'rgba(145,160,180,.4)';
      x.beginPath(); x.arc(px + rnd() * w, py + rnd() * h, 1.6 + rnd() * 2.4, 0, 7); x.fill();
    }
  };
  const wLog = (px, py, w, h) => {
    const rows = Math.min(7, Math.floor(h / 15));
    for (let i = 0; i < rows; i++) {
      const ry = py + i * 15;
      x.fillStyle = i === 0 ? acc : 'rgba(145,160,180,.3)'; x.fillRect(px, ry + 5, 3, 3);
      x.fillStyle = `rgba(180,192,208,${0.5 - i * 0.04})`;
      x.fillRect(px + 8, ry + 5, w * (0.3 + rnd() * 0.62), 3);
    }
  };
  const wSpark = (px, py, w, h) => {
    const k = ri(3, 4), rh = h / k;
    for (let r = 0; r < k; r++) {
      const d = series(12, 0.2, 1), sx = w * 0.55 / (d.length - 1);
      x.font = '9px ui-monospace,Menlo,monospace'; x.textAlign = 'left';
      x.fillStyle = '#6f7a89'; x.fillText(pick(PANEL).slice(0, 8), px, py + r * rh + rh * 0.62);
      x.beginPath();
      d.forEach((v, i) => {
        const X = px + w * 0.42 + i * sx, Y = py + r * rh + rh * 0.7 - rh * 0.42 * v;
        i ? x.lineTo(X, Y) : x.moveTo(X, Y);
      });
      x.strokeStyle = r === 0 ? acc : 'rgba(145,160,180,.45)'; x.lineWidth = 1.3; x.stroke();
    }
  };
  const WIDGETS = [wBars, wHBars, (a, b, c, d) => wLine(a, b, c, d, true),
    (a, b, c, d) => wLine(a, b, c, d, false), wDonut, wKPI, wKPIGrid, wTable,
    wHeat, wGauge, wStack, wScatter, wLog, wSpark];

  const regen = () => {
    leaves = [];
    split([PAD, HEAD, CW - PAD * 2, CH - HEAD - PAD], 3, leaves, 2);
    acc = pick(ACCENTS); title = pick(TITLES); sub = pick(SUBS);
    leaves.forEach(l => { l.w = WIDGETS[Math.floor(rnd() * WIDGETS.length)]; l.t = pick(PANEL); });
  };

  const draw = () => {
    x.fillStyle = '#12151a'; x.fillRect(0, 0, CW, CH);
    x.textAlign = 'left';
    x.font = '600 15px system-ui,-apple-system,Segoe UI,sans-serif';
    x.fillStyle = '#e7ecf3'; x.fillText(title, PAD + 2, 26);
    x.font = '11.5px system-ui,-apple-system,Segoe UI,sans-serif';
    x.fillStyle = '#6f7a89'; x.fillText(sub, PAD + 2, 40);
    x.fillStyle = acc; x.fillRect(CW - PAD - 56, 17, 56, 3);

    leaves.forEach(L => {
      const px = L[0] + 5, py = L[1] + 5, w = L[2] - 10, h = L[3] - 10;
      x.fillStyle = '#171b22'; x.fillRect(px, py, w, h);
      x.strokeStyle = '#252c37'; x.lineWidth = 1; x.strokeRect(px + .5, py + .5, w - 1, h - 1);
      x.font = '9.5px ui-monospace,SFMono-Regular,Menlo,monospace';
      x.textAlign = 'left'; x.fillStyle = '#6f7a89';
      x.fillText(L.t.toUpperCase(), px + 9, py + 16);
      const ix = px + 9, iy = py + 24, iw = w - 18, ih = h - 33;
      if (iw > 20 && ih > 16) {
        x.save(); x.beginPath(); x.rect(ix, iy, iw, ih); x.clip();
        L.w(ix, iy, iw, ih); x.restore();
      }
    });
  };

  let last = 0;
  const loop = ts => {
    if (ts - last > 600) { regen(); draw(); last = ts; }
    raf = requestAnimationFrame(loop);
  };
  regen(); draw(); raf = requestAnimationFrame(loop);
  return () => cancelAnimationFrame(raf);
};

export const DashboardGen = () => {
  const ref = useCanvas(runGenerator);
  return <Frame
    path='/views/*'
    caption={<><b>Figure 4.</b> Illustrative layouts, showing the range a document can take rather than any single deliverable.</>}>
    <div className='tp-fig-gen'><canvas ref={ref} width={900} height={400}/></div>
  </Frame>;
};
